import React, { useEffect, useRef, useState } from 'react';
import './TicketConversation.scss';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MainContainer, ChatContainer, MessageList } from "@chatscope/chat-ui-kit-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchComments, postComment, clearComment } from '../../../../../slices/TicketSlice'; 
import PersonIcon from "@mui/icons-material/Person";
import { Avatar, TextField } from "@mui/material";

export default function TicketConversation({ sortOrder = 'asc', onClose }) {
  const dispatch = useDispatch();
  const conversationEndRef = useRef(null);
  const inputRef = useRef(null);
  const COMMENT_TOAST_ID = "comments-error";

  const [value, setValue] = useState("");
  const limit = 1000;
  const currentUser = useSelector((state) => state.user);
  const sanitizeInput = (text) => {
  return text.replace(/<iframe[^>]*>.*?<\/iframe>|<iframe[^>]*\/>/gi, '');
};
  // Exact character counting for plain text
  const trueLength = value.length;
  const isOverLimit = trueLength > limit;
  const reachedLimit = trueLength >= limit;

  // Selected ticket ID and comments from Redux
  const ticketId = useSelector((state) => state.tickets.selectedTicket?.id);
  const ticket = useSelector((state) => state.tickets.selectedTicket);
  const { comments: slicedComments, commentsStatus, error } = useSelector(state => state.tickets);
  
  const comments = (slicedComments || []).map(c => {
    const loggedInId = currentUser?.user?.id || currentUser?.id;
    const isOutgoing = 
      c.authorId === loggedInId ||
      c.userid === loggedInId ||
      c.userId === loggedInId ||
      c.type === "outgoing";
    return {
      ...c,
      type: isOutgoing ? "outgoing" : "incoming"
    };
  });

  useEffect(() => {
    if (!ticketId) return;

    dispatch(fetchComments({ ticketId, sortOrder }));

    return () => {
      dispatch(clearComment());
    };
  }, [sortOrder, dispatch, ticketId]); 

  useEffect(() => {
    if (conversationEndRef.current) {
      conversationEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [comments]);

  // display toast error if fetching comments failed
  if (commentsStatus === "failed" && !toast.isActive(COMMENT_TOAST_ID)) {
    toast.error(error || "Failed to load comments.", { toastId: COMMENT_TOAST_ID });
  }


  // display toast if no ticket is selected
  if (!ticketId) {
    toast.info("Select a ticket to view the conversation.");
  }

  // Handle Send action
  const handleSend = () => {
    const trimmedText = value.trim();

    if (!trimmedText || trueLength > limit) {
      toast.error("Comment cannot be empty or contain only whitespace!");
      return;
    }

    /// Dispatch the action
    dispatch(postComment({ ticketId, comment: trimmedText }))
      .unwrap()
      .then(() => {
        // Clear the state only if the request succeeded
        setValue(""); 
        setTimeout(() => {
          inputRef.current?.focus?.();
        }, 0);
      })
      .catch(() => {
        toast.error("Failed to send comment!");
      });
  };

  const groupComments = (comments, timeWindowMinutes = 5) => {
    if (!comments || comments.length === 0) return [];

    const groups = [];
    let currentGroup = { sender: comments[0].sender, type: comments[0].type, messages: [], startTime: comments[0].sentTime };

    comments.forEach((comment, i) => {
      const prev = i > 0 ? comments[i - 1] : null;

      const currentTime = comment.sentTime ? new Date(comment.sentTime).getTime() : 0;
      const prevTime = prev ? new Date(prev.sentTime).getTime() : 0;
      const diffMinutes = (currentTime - prevTime) / 1000 / 60;

      if (
        // Same sender and within time window
        prev &&
        comment.sender === prev.sender &&
        comment.type === prev.type &&
        diffMinutes <= timeWindowMinutes
      ) {
        currentGroup.messages.push(comment);
      } else {
        // New group
        if (currentGroup.messages.length > 0) groups.push(currentGroup);
        currentGroup = { sender: comment.sender, type: comment.type, messages: [comment], startTime: comment.sentTime };
      }
    });

    if (currentGroup.messages.length > 0) groups.push(currentGroup);
    // Push last group
    return groups;
  };

  const handleCopyCommentText = (e) => {
    const selectedText = window.getSelection().toString();
    if (!selectedText) return;
    e.preventDefault();
    e.clipboardData.setData("text/plain", selectedText);
  };

  const [isSmallScreen, setIsSmallScreen] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 400;
  });

  const maxLimitMsg = isSmallScreen ? "Max Characters" : "Max Characters Reached";

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mediaQuery = window.matchMedia("(max-width: 399px)");
    const handleChange = (e) => setIsSmallScreen(e.matches);

    setIsSmallScreen(mediaQuery.matches);
    mediaQuery.addEventListener?.("change", handleChange);

    return () => mediaQuery.removeEventListener?.("change", handleChange);
  }, []);

  return (
    <>
     <div
      className="ticketDiscussionBackdrop"
      onClick={onClose}
      data-testid="ticket-discussion-backdrop"
    />  
    <div className='TicketDiscussion' data-testid="ticket-discussion">
      <button
        data-testid="CloseBtn"
        className="ExitConversationBtn"
        onClick={onClose}
      >
        X
      </button>   

      <div className="conversation-container" data-testid="conversation-container">
        {/* Sender info at the top of the group */}
        <MainContainer className="main-container" data-testid="main-container">
          <ChatContainer className="chat-container" data-testid="chat-container">
            <MessageList className="message-list" data-testid="message-list" scrollable={true}>
              {comments.length === 0 && (
                <p className="empty" data-testid="empty-comments-message">No comments yet.</p>
              )}
              {groupComments(comments).map((group, i) => (
                <div 
                  key={i}
                  className={`message-row ${group.type === "outgoing" ? "outgoing" : "incoming"}`}
                  data-testid={`message-group-${i}`}
                >
                  <div className="message-meta" data-testid={`message-meta-${i}`}>
                    <span className="sender-name" data-testid={`sender-name-${i}`}>{group.sender}</span>
                    <span className="sent-time" data-testid={`sent-time-${i}`}>
                      {group.startTime.replace(/:(\d{2}) (?=AM|PM)/, " ")}
                    </span>
                  </div>
                  {/* Render each message in the group using existing bubble styles */}
                  {group.messages.map((m, j) => {
                    const targetUrl = m.profilePictureURL || m.profilePictureUrl;
                    const rawUrl = group.type === "outgoing"
                      ? (currentUser?.user?.profilePictureURL || currentUser?.profilePictureURL || currentUser?.currentUser?.profilePictureURL)
                      : targetUrl;

                    const avatarSrc = (rawUrl && rawUrl !== "undefined" && rawUrl !== "null") ? rawUrl : null;

                    return (
                      <div key={j} className="single-message-wrapper">
                        <Avatar 
                          className="chat-message-avatar"
                          src={avatarSrc || ""} 
                          alt={group.sender}
                        >
                          {!avatarSrc && <PersonIcon sx={{ color: "#6e2e76" }} />}
                        </Avatar>
                        <div
                          className={`message-bubble ${m.type === "outgoing" ? "message-outgoing" : "message-incoming"}`}
                          data-testid={`message-${i}-${j}`}
                          onCopy={handleCopyCommentText}
                          dangerouslySetInnerHTML={{
                            __html: (m.message || "")
                              .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
                              .replace(/\n/g, "<br/>")
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
              <div ref={conversationEndRef} />
            </MessageList>
          </ChatContainer>

          {/* Clean MUI TextField & Button input area */}
          <div className="msg-input">
            <textarea
              className="native-chat-textarea"
              data-testid="message-input"
              rows={1}
              placeholder={
                ticket?.status === "CLOSED"
                  ? "Ticket is closed. You cannot add comments." 
                  : "Add a comment (max = 1000 characters)..."
              }
              value={value}
              ref={inputRef}
              disabled={ticket?.status === "CLOSED"}
              maxLength={limit}
              onChange={(e) => {
                const cleanValue = sanitizeInput(e.target.value);
                setValue(cleanValue);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <button
              type="button"
              className="native-send-btn"
              onClick={handleSend}
              disabled={ticket?.status === "CLOSED" || value.trim().length === 0 || isOverLimit}
              data-testid="send-comment-button"
            >
              Send
            </button>
          </div>

          <div 
            className="length-indicator" 
            data-testid="length-indicator"
            style={{ display: 'flex', justifyContent: 'space-between', color: isOverLimit ? 'red' : 'gray', marginLeft: '6px' }}
          >
            <span
              data-testid="max-char-msg"
              style={{ 
                visibility: reachedLimit ? 'visible' : 'hidden',
                color: 'red'
              }}
            >
              {maxLimitMsg}
            </span>
            <span>{trueLength}/{limit}</span>
          </div>
        </MainContainer>
      </div>
    </div>
    </>
  );
}